import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { TimePicker } from '@/components/ui/time-picker';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, MapPin, Upload, FileText, Sparkles, X } from 'lucide-react';
import { DatePickerField } from '@/components/shipment';

const LOAD_TYPES = [
  { value: 'FTL', label: 'FTL (Full Truckload)' },
  { value: 'LTL', label: 'LTL (Less Than Truckload)' },
  { value: 'Partial', label: 'Partial Load' },
  { value: 'Dedicated', label: 'Dedicated' }
];

const LOCATION_TYPES = [
  { value: 'Customer', label: 'Customer' },
  { value: 'Warehouse', label: 'Warehouse' },
  { value: 'Distribution Center', label: 'Distribution Center' },
  { value: 'Yard', label: 'Yard' },
  { value: 'Port', label: 'Port' },
  { value: 'Terminal', label: 'Terminal' },
  { value: 'Cross Dock', label: 'Cross Dock' }
];

const getInitialFormData = () => ({
  load_type: 'FTL',
  origin_type: '',
  destination_type: '',
  pickup_address: '',
  pickup_city: '',
  pickup_state: '',
  pickup_zip: '',
  pickup_contact: '',
  pickup_phone: '',
  pickup_date: null,
  pickup_time_from: '',
  pickup_time_to: '',
  delivery_address: '',
  delivery_city: '',
  delivery_state: '',
  delivery_zip: '',
  delivery_contact: '',
  delivery_phone: '',
  delivery_date: null,
  delivery_time_from: '',
  delivery_time_to: '',
  is_multi_stop: false,
  stops: [],
  truck_id: '',
  trailer_id: '',
  trailer_type: '',
  driver_id: '',
  driver_name: '',
  customer_id: '',
  customer_name: '',
  rate: 0,
  weight: 0,
  commodity: '',
  special_instructions: '',
  is_important: false,
  remarks: ''
});

const emptyStop = () => ({
  id: Date.now().toString(),
  type: 'Delivery',
  location_type: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  contact: '',
  phone: '',
  date: null,
  time_from: '',
  time_to: '',
  notes: ''
});

export const AddLocalLoad = ({ open, onOpenChange, masterData, customers = [], onSuccess }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData());
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [trailerTypes, setTrailerTypes] = useState([]);
  
  // AI DO Upload
  const [uploadingDO, setUploadingDO] = useState(false);
  const [uploadedDOFile, setUploadedDOFile] = useState(null);
  const doFileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetchEquipment();
    }
  }, [open]);

  const fetchEquipment = async () => {
    try {
      const [trucksRes, trailersRes, typesRes] = await Promise.all([
        api.get('/local-loads/equipment/trucks'),
        api.get('/local-loads/equipment/trailers'),
        api.get('/local-loads/equipment/trailer-types')
      ]);
      setTrucks(trucksRes.data || []);
      setTrailers(trailersRes.data || []);
      setTrailerTypes(typesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch equipment:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddStop = () => {
    setFormData(prev => ({
      ...prev,
      stops: [...prev.stops, emptyStop()]
    }));
  };

  const handleRemoveStop = (stopId) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.filter(s => s.id !== stopId)
    }));
  };

  const handleStopChange = (stopId, field, value) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.map(s => s.id === stopId ? { ...s, [field]: value } : s)
    }));
  };

  // Handle DO document upload for AI extraction
  const handleDOUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().match(/\.(pdf|png|jpg|jpeg)$/)) {
      toast.error('Please upload a PDF or image file (PNG, JPG)');
      return;
    }
    
    setUploadingDO(true);
    setUploadedDOFile(file);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      toast.info('AI is analyzing the document...', { duration: 5000 });
      
      const response = await api.post('/ai/extract-do', formDataUpload);
      
      if (response.data.success) {
        const extracted = response.data.data;
        
        setFormData(prev => ({
          ...prev,
          // Pickup location
          pickup_address: extracted.pickup_address || extracted.delivery_address || prev.pickup_address,
          pickup_city: extracted.pickup_city || extracted.delivery_city || prev.pickup_city,
          pickup_state: extracted.pickup_state || extracted.delivery_state || prev.pickup_state,
          pickup_zip: extracted.pickup_zip || extracted.delivery_zip || prev.pickup_zip,
          pickup_contact: extracted.pickup_contact || extracted.delivery_contact || prev.pickup_contact,
          pickup_phone: extracted.pickup_phone || extracted.delivery_phone || prev.pickup_phone,
          // Delivery location (use secondary if available)
          delivery_address: extracted.secondary_address || prev.delivery_address,
          delivery_city: extracted.secondary_city || prev.delivery_city,
          delivery_state: extracted.secondary_state || prev.delivery_state,
          delivery_zip: extracted.secondary_zip || prev.delivery_zip,
          delivery_contact: extracted.secondary_contact || prev.delivery_contact,
          delivery_phone: extracted.secondary_phone || prev.delivery_phone,
          // Customer
          customer_name: extracted.customer_name || prev.customer_name,
          // Cargo
          weight: extracted.weight ? parseFloat(extracted.weight) : prev.weight,
          commodity: extracted.commodity || extracted.remarks || prev.commodity,
          remarks: extracted.remarks ? 
            `${prev.remarks ? prev.remarks + '\n' : ''}${extracted.remarks}` : 
            prev.remarks
        }));
        
        // Customer matching
        if (extracted.customer_name && customers.length > 0) {
          const customerName = extracted.customer_name.toLowerCase();
          const matchedCustomer = customers.find(c => 
            (c.name || '').toLowerCase().includes(customerName) || 
            customerName.includes((c.name || '').toLowerCase())
          );
          if (matchedCustomer) {
            setFormData(prev => ({ ...prev, customer_id: matchedCustomer.id, customer_name: matchedCustomer.name }));
          } else {
            toast.warning(
              `Customer "${extracted.customer_name}" not found. You can create them in Master Tables > Customers.`,
              { duration: 8000 }
            );
          }
        }
        
        const fieldsExtracted = Object.values(extracted).filter(v => v !== null && v !== '').length;
        toast.success(`Document analyzed! ${fieldsExtracted} fields extracted`);
      } else {
        toast.error(response.data.error || 'Failed to extract data from document');
      }
    } catch (error) {
      console.error('DO extraction error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process document');
    } finally {
      setUploadingDO(false);
      if (doFileInputRef.current) doFileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.pickup_address || !formData.delivery_address) {
      toast.error('Pickup and delivery addresses are required');
      return;
    }

    setLoading(true);
    try {
      const payload = { ...formData };
      
      // Convert dates
      if (payload.pickup_date) payload.pickup_date = payload.pickup_date.toISOString();
      if (payload.delivery_date) payload.delivery_date = payload.delivery_date.toISOString();
      
      // Convert stop dates
      if (payload.stops) {
        payload.stops = payload.stops.map(s => ({
          ...s,
          date: s.date ? s.date.toISOString() : null
        }));
      }

      await api.post('/local-loads', payload);
      toast.success('Local load created successfully');
      onSuccess?.();
      setFormData(getInitialFormData());
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create local load');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[550px] sm:w-[650px] sm:max-w-[650px] bg-slate-900 border-slate-800 overflow-y-auto" data-testid="add-local-load-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading text-white flex items-center gap-2">
            <MapPin className="h-5 w-5 text-green-400" />
            Add Local Load
          </SheetTitle>
          <SheetDescription className="text-slate-400">Create a new local/national load</SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
          {/* AI Document Upload */}
          <div className="p-4 bg-gradient-to-r from-cyan-900/30 to-blue-900/30 rounded-lg border border-cyan-500/30">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="h-5 w-5 text-cyan-400" />
              <h3 className="font-medium text-white">Upload Delivery Order</h3>
            </div>
            <p className="text-slate-400 text-sm mb-3">Upload a DO image to auto-fill fields with AI</p>
            <div className="flex items-center gap-3">
              <input 
                type="file" 
                ref={doFileInputRef} 
                onChange={handleDOUpload} 
                accept=".pdf,.png,.jpg,.jpeg" 
                className="hidden" 
              />
              <Button
                type="button"
                onClick={() => doFileInputRef.current?.click()}
                disabled={uploadingDO}
                variant="outline"
                className="border-cyan-500/50 hover:border-cyan-400 hover:bg-cyan-500/10"
                data-testid="upload-local-do-btn"
              >
                {uploadingDO ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing...</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" />Upload DO</>
                )}
              </Button>
              {uploadedDOFile && !uploadingDO && (
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <FileText className="h-4 w-4" />
                  <span>{uploadedDOFile.name}</span>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setUploadedDOFile(null)} 
                    className="h-6 w-6 p-0 text-slate-400 hover:text-white"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
            <p className="text-slate-500 text-xs mt-2">Supports PDF, PNG, JPG files</p>
          </div>

          {/* Load Type */}
          <div className="space-y-4">
            <h3 className="font-medium text-white border-b border-slate-800 pb-2">Load Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Load Type *</Label>
                <Select value={formData.load_type} onValueChange={(v) => handleChange('load_type', v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {LOAD_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Customer</Label>
                <Select value={formData.customer_id || "none"} onValueChange={(v) => {
                  const customer = customers.find(c => c.id === v);
                  handleChange('customer_id', v === "none" ? "" : v);
                  handleChange('customer_name', customer?.name || '');
                }}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                    <SelectItem value="none" className="text-slate-400">No customer</SelectItem>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id} className="text-white">{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Pickup Section */}
          <div className="space-y-4 p-4 bg-green-900/20 rounded-lg border border-green-500/30">
            <h3 className="font-medium text-green-400">Pickup Location</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Location Type</Label>
                <Select value={formData.origin_type || "none"} onValueChange={(v) => handleChange('origin_type', v === "none" ? "" : v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="none" className="text-slate-400">Select type</SelectItem>
                    {LOCATION_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DatePickerField label="Pickup Date" value={formData.pickup_date} onChange={handleChange} field="pickup_date" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Address *</Label>
              <AddressAutocomplete
                value={formData.pickup_address}
                onChange={(val) => handleChange('pickup_address', val)}
                onAddressSelect={(addr) => {
                  handleChange('pickup_address', addr.street || addr.formatted_address);
                  if (addr.city) handleChange('pickup_city', addr.city);
                  if (addr.state) handleChange('pickup_state', addr.state);
                  if (addr.zip) handleChange('pickup_zip', addr.zip);
                }}
                placeholder="Enter pickup address"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Input value={formData.pickup_city} onChange={(e) => handleChange('pickup_city', e.target.value)} placeholder="City" className="bg-slate-950 border-slate-800 text-white" />
              <Input value={formData.pickup_state} onChange={(e) => handleChange('pickup_state', e.target.value)} placeholder="State" className="bg-slate-950 border-slate-800 text-white" />
              <Input value={formData.pickup_zip} onChange={(e) => handleChange('pickup_zip', e.target.value)} placeholder="ZIP" className="bg-slate-950 border-slate-800 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input value={formData.pickup_contact} onChange={(e) => handleChange('pickup_contact', e.target.value)} placeholder="Contact name" className="bg-slate-950 border-slate-800 text-white" />
              <Input value={formData.pickup_phone} onChange={(e) => handleChange('pickup_phone', e.target.value)} placeholder="Phone" className="bg-slate-950 border-slate-800 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Time From</Label>
                <TimePicker value={formData.pickup_time_from} onChange={(v) => handleChange('pickup_time_from', v)} placeholder="From" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Time To</Label>
                <TimePicker value={formData.pickup_time_to} onChange={(v) => handleChange('pickup_time_to', v)} placeholder="To" />
              </div>
            </div>
          </div>

          {/* Delivery Section */}
          <div className="space-y-4 p-4 bg-blue-900/20 rounded-lg border border-blue-500/30">
            <h3 className="font-medium text-blue-400">Delivery Location</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Location Type</Label>
                <Select value={formData.destination_type || "none"} onValueChange={(v) => handleChange('destination_type', v === "none" ? "" : v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="none" className="text-slate-400">Select type</SelectItem>
                    {LOCATION_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DatePickerField label="Delivery Date" value={formData.delivery_date} onChange={handleChange} field="delivery_date" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Address *</Label>
              <AddressAutocomplete
                value={formData.delivery_address}
                onChange={(val) => handleChange('delivery_address', val)}
                onAddressSelect={(addr) => {
                  handleChange('delivery_address', addr.street || addr.formatted_address);
                  if (addr.city) handleChange('delivery_city', addr.city);
                  if (addr.state) handleChange('delivery_state', addr.state);
                  if (addr.zip) handleChange('delivery_zip', addr.zip);
                }}
                placeholder="Enter delivery address"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Input value={formData.delivery_city} onChange={(e) => handleChange('delivery_city', e.target.value)} placeholder="City" className="bg-slate-950 border-slate-800 text-white" />
              <Input value={formData.delivery_state} onChange={(e) => handleChange('delivery_state', e.target.value)} placeholder="State" className="bg-slate-950 border-slate-800 text-white" />
              <Input value={formData.delivery_zip} onChange={(e) => handleChange('delivery_zip', e.target.value)} placeholder="ZIP" className="bg-slate-950 border-slate-800 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input value={formData.delivery_contact} onChange={(e) => handleChange('delivery_contact', e.target.value)} placeholder="Contact name" className="bg-slate-950 border-slate-800 text-white" />
              <Input value={formData.delivery_phone} onChange={(e) => handleChange('delivery_phone', e.target.value)} placeholder="Phone" className="bg-slate-950 border-slate-800 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Time From</Label>
                <TimePicker value={formData.delivery_time_from} onChange={(v) => handleChange('delivery_time_from', v)} placeholder="From" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Time To</Label>
                <TimePicker value={formData.delivery_time_to} onChange={(v) => handleChange('delivery_time_to', v)} placeholder="To" />
              </div>
            </div>
          </div>

          {/* Multi-Stop Toggle */}
          <div className="flex items-center gap-3 py-2">
            <Checkbox 
              id="is_multi_stop" 
              checked={formData.is_multi_stop} 
              onCheckedChange={(checked) => handleChange('is_multi_stop', checked)} 
              className="border-orange-500"
            />
            <Label htmlFor="is_multi_stop" className="text-orange-400 font-medium cursor-pointer">Multi-Stop Load</Label>
          </div>

          {/* Additional Stops */}
          {formData.is_multi_stop && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-orange-400">Additional Stops</h3>
                <Button type="button" size="sm" variant="outline" onClick={handleAddStop} className="border-orange-500 text-orange-400">
                  <Plus className="h-4 w-4 mr-1" />Add Stop
                </Button>
              </div>
              {formData.stops.map((stop, index) => (
                <div key={stop.id} className="p-4 bg-orange-900/20 rounded-lg border border-orange-500/30 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-orange-400 font-medium">Stop #{index + 1}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveStop(stop.id)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={stop.type} onValueChange={(v) => handleStopChange(stop.id, 'type', v)}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="Pickup" className="text-white">Pickup</SelectItem>
                        <SelectItem value="Delivery" className="text-white">Delivery</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={stop.location_type || "none"} onValueChange={(v) => handleStopChange(stop.id, 'location_type', v === "none" ? "" : v)}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Location type" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="none" className="text-slate-400">Select type</SelectItem>
                        {LOCATION_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <AddressAutocomplete
                    value={stop.address}
                    onChange={(val) => handleStopChange(stop.id, 'address', val)}
                    onAddressSelect={(addr) => {
                      handleStopChange(stop.id, 'address', addr.street || addr.formatted_address);
                      if (addr.city) handleStopChange(stop.id, 'city', addr.city);
                      if (addr.state) handleStopChange(stop.id, 'state', addr.state);
                      if (addr.zip) handleStopChange(stop.id, 'zip', addr.zip);
                    }}
                    placeholder="Enter address"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <Input value={stop.city} onChange={(e) => handleStopChange(stop.id, 'city', e.target.value)} placeholder="City" className="bg-slate-950 border-slate-800 text-white" />
                    <Input value={stop.state} onChange={(e) => handleStopChange(stop.id, 'state', e.target.value)} placeholder="State" className="bg-slate-950 border-slate-800 text-white" />
                    <Input value={stop.zip} onChange={(e) => handleStopChange(stop.id, 'zip', e.target.value)} placeholder="ZIP" className="bg-slate-950 border-slate-800 text-white" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Equipment */}
          <div className="space-y-4">
            <h3 className="font-medium text-white border-b border-slate-800 pb-2">Equipment</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Truck</Label>
                <Select value={formData.truck_id || "none"} onValueChange={(v) => handleChange('truck_id', v === "none" ? "" : v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select truck" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="none" className="text-slate-400">No truck</SelectItem>
                    {trucks.map(t => (
                      <SelectItem key={t.id} value={t.id} className="text-white">{t.number} - {t.make} {t.model}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Trailer</Label>
                <Select value={formData.trailer_id || "none"} onValueChange={(v) => handleChange('trailer_id', v === "none" ? "" : v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select trailer" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="none" className="text-slate-400">No trailer</SelectItem>
                    {trailers.map(t => (
                      <SelectItem key={t.id} value={t.id} className="text-white">{t.number} - {t.type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Trailer Type</Label>
                <Select value={formData.trailer_type || "none"} onValueChange={(v) => handleChange('trailer_type', v === "none" ? "" : v)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="none" className="text-slate-400">Select type</SelectItem>
                    {trailerTypes.map(t => (
                      <SelectItem key={t.id} value={t.name} className="text-white">{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Driver & Rate */}
          <div className="space-y-4">
            <h3 className="font-medium text-white border-b border-slate-800 pb-2">Assignment & Rates</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Driver</Label>
                <Select value={formData.driver_id || "none"} onValueChange={(v) => {
                  const driver = masterData?.drivers?.find(d => d.id === v);
                  handleChange('driver_id', v === "none" ? "" : v);
                  handleChange('driver_name', driver?.name || '');
                }}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select driver" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="none" className="text-slate-400">No driver</SelectItem>
                    {masterData?.drivers?.map(d => (
                      <SelectItem key={d.id} value={d.id} className="text-white">{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Rate ($)</Label>
                <Input type="number" value={formData.rate} onChange={(e) => handleChange('rate', parseFloat(e.target.value) || 0)} placeholder="0.00" className="bg-slate-950 border-slate-800 text-white" />
              </div>
            </div>
          </div>

          {/* Cargo */}
          <div className="space-y-4">
            <h3 className="font-medium text-white border-b border-slate-800 pb-2">Cargo Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Weight (lbs)</Label>
                <Input type="number" value={formData.weight} onChange={(e) => handleChange('weight', parseFloat(e.target.value) || 0)} placeholder="0" className="bg-slate-950 border-slate-800 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Commodity</Label>
                <Input value={formData.commodity} onChange={(e) => handleChange('commodity', e.target.value)} placeholder="e.g., General Freight" className="bg-slate-950 border-slate-800 text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Special Instructions</Label>
              <Textarea value={formData.special_instructions} onChange={(e) => handleChange('special_instructions', e.target.value)} placeholder="Any special handling instructions..." className="bg-slate-950 border-slate-800 text-white min-h-[60px]" />
            </div>
          </div>

          {/* Important & Remarks */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Checkbox checked={formData.is_important} onCheckedChange={(c) => handleChange('is_important', c)} className="border-red-500" />
              <Label className="text-red-400 font-medium">IMPORTANT LOAD</Label>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Remarks</Label>
              <Textarea value={formData.remarks} onChange={(e) => handleChange('remarks', e.target.value)} placeholder="Additional notes..." className="bg-slate-950 border-slate-800 text-white min-h-[60px]" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-slate-800">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 border-slate-700">Cancel</Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-green-600 hover:bg-green-500" data-testid="save-local-load-btn">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save Local Load
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default AddLocalLoad;
